import { useEffect } from 'react';

import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded';
import PropTypes from 'prop-types';

import useAsyncCallback from '@zenbusiness/application-commons-hooks/useAsyncCallback';
import { useIntl } from '@zenbusiness/application-commons-intl';
import { usePriceList } from '@zenbusiness/application-commons-pricelist';
import Stack from '@zenbusiness/zui/Stack';

import CalloutAsset from '#client/components/CalloutAsset';
import CalloutIcon from '#client/components/CalloutIcon';
import ExtraInfoCard from '#client/components/ExtraInfoCard';
import { formatOptionsForCart } from '#client/components/MultipleJurisdictionsSelection';
import PageHeader from '#client/components/PageHeader';
import ProductOfferView from '#client/components/ProductOfferView';
import useFlowState from '#client/hooks/flow/useFlowState';
import useAddToCart from '#client/hooks/useAddToCart';
import useFlowNavigation from '#client/hooks/useFlowNavigation';
import useProductOfferViewProps from '#client/hooks/useProductOfferViewProps';
import useRemoveFromCart from '#client/hooks/useRemoveFromCart';
import TwoColumnLayout from '#client/layouts/TwoColumnLayout';

const MESSAGE_KEY = 'shopping.page.basicCompliance';
const ADVANCED_COMPLIANCE = 'worry_free_service_advanced_addon';
export const BASIC_COMPLIANCE = 'worry_free_service_basic';
export const PRICE_ID = 'basicCompliance';

function BasicCompliancePage() {
  const { goToNextPage } = useFlowNavigation();
  const {
    businessInfo: { cora, previousJurisdictionState }
  } = useFlowState();
  const { formatMessage } = useIntl();

  // Adding advanced compliance to exclusive services to prevent the user
  // from having both basic and advanced compliance in the cart at the same time
  const { addedToCart, addToCart } = useAddToCart({
    exclusiveServices: [ADVANCED_COMPLIANCE],
    protectedServices: [BASIC_COMPLIANCE]
  });
  const { removedFromCart, removeFromCart } = useRemoveFromCart();
  const { loading: priceListLoading, get: getProduct, priceListUuid } = usePriceList();
  const product = getProduct(BASIC_COMPLIANCE);
  // Getting the cora multiple jurisdictions to add the services to the cart
  const { jurisdictions: selectedStates = [] } = cora || {};

  if (!priceListLoading && !product) {
    throw new Error(`Could not find ${BASIC_COMPLIANCE} in pricing list ${priceListUuid}`);
  }

  const { servicePricingUuid, price } = product.pricing;

  useEffect(() => {
    if (addedToCart || removedFromCart) {
      goToNextPage();
    }
  }, [addedToCart, goToNextPage, removedFromCart]);

  const handlePrimaryActionClick = useAsyncCallback(async () => {
    // Remove the previous jurisdiction from the cart if it exists
    if (previousJurisdictionState) {
      await removeFromCart({
        servicePricingUuid: product.pricing?.servicePricingUuid,
        jurisdictionState: previousJurisdictionState
      });
    }

    // Add the service to the cart for each selected jurisdiction
    const itemsToAdd = formatOptionsForCart(
      selectedStates,
      product.pricing?.servicePricingUuid,
      product.service
    );
    await addToCart(itemsToAdd);
  }, [addToCart, servicePricingUuid, selectedStates]);

  const handleSecondaryActionClick = useAsyncCallback(async () => {
    // Build the items to remove from the cart for the current jurisdiction
    const itemsToRemove = selectedStates.map((element) => ({
      servicePricingUuid,
      jurisdictionState: element.value
    }));
    await removeFromCart(itemsToRemove);
  }, [removeFromCart, servicePricingUuid]);

  const productOfferViewProps = useProductOfferViewProps({
    id: PRICE_ID,
    itemAssets: ['notificationsrounded', 'calendartodayrounded', 'donutlargerounded'],
    noteValues: { price },
    price,
    primaryAction: {
      'data-testid': 'bc-primary-action',
      onClick: handlePrimaryActionClick
    },
    secondaryAction: {
      'data-testid': 'bc-secondary-action',
      onClick: handleSecondaryActionClick
    }
  });

  return (
    <TwoColumnLayout>
      <TwoColumnLayout.SmallColumn>
        <PageHeader
          subtitle={formatMessage({
            id: 'shopping.pageHeader.subtitle.basicCompliance'
          })}
          title={formatMessage({ id: 'shopping.pageHeader.title.basicCompliance' })}
        />
        <ProductOfferView {...productOfferViewProps} />
      </TwoColumnLayout.SmallColumn>
      <TwoColumnLayout.LargeColumn>
        <Stack spacing={2}>
          <ExtraInfoCard messageKey={MESSAGE_KEY} />
          <CalloutAsset alt="basic compliance" name="basic-compliance-screenshot" />
          <CalloutIcon
            description={formatMessage({ id: `${MESSAGE_KEY}.callout.subtitle` })}
            icon={<PlaylistAddCheckRoundedIcon sx={{ fontSize: 32 }} />}
          />
        </Stack>
      </TwoColumnLayout.LargeColumn>
    </TwoColumnLayout>
  );
}

BasicCompliancePage.propTypes = {
  couponCode: PropTypes.string
};

export default BasicCompliancePage;
